// Metal-glass toast system.
//
// Sits as a fixed bottom-right stack. Toasts slide in from the right,
// auto-dismiss after `duration` ms (default 4500), and can be closed
// manually with the × button.
//
// Usage:
//   import { toast } from "@/components/toast";
//   toast.error("Couldn't save"); toast.success("Saved");
//
// Plus mount <Toaster /> once near the root. The store lives in module
// scope (no React context needed) so toasts can be fired from outside
// the React tree — e.g. inside react-query onError handlers, fetch
// helpers, or other side-effect code.

import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { useSyncExternalStore } from "react";

type ToastKind = "success" | "error" | "info";

interface Toast {
	id: string;
	kind: ToastKind;
	message: string;
	/** When defined, auto-dismiss after this many ms. */
	duration: number | null;
	/** Optional title above the message. */
	title?: string;
	/** Wall-clock create time so we can fade older toasts if the stack gets long. */
	createdAt: number;
}

// ---------------------------------------------------------------------------
// Tiny external store

let state: Toast[] = [];
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
	listeners.add(cb);
	return () => {
		listeners.delete(cb);
	};
}

function getSnapshot(): Toast[] {
	return state;
}

function emit() {
	for (const l of listeners) l();
}

function push(t: Omit<Toast, "id" | "createdAt">): string {
	const id = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
	const toastObj: Toast = { ...t, id, createdAt: Date.now() };
	state = [...state, toastObj];
	emit();
	if (toastObj.duration && toastObj.duration > 0) {
		setTimeout(() => dismiss(id), toastObj.duration);
	}
	return id;
}

function dismiss(id: string): void {
	state = state.filter((t) => t.id !== id);
	emit();
}

// ---------------------------------------------------------------------------
// Public API

interface ToastOpts {
	title?: string;
	/** ms; pass null for sticky toasts that only close on × click. */
	duration?: number | null;
}

const DEFAULT_DURATION = 4500;

export const toast = {
	success(message: string, opts: ToastOpts = {}) {
		return push({
			kind: "success",
			message,
			title: opts.title,
			duration: opts.duration ?? DEFAULT_DURATION,
		});
	},
	error(message: string, opts: ToastOpts = {}) {
		return push({
			kind: "error",
			message,
			title: opts.title,
			// Errors stay around longer by default — they usually carry
			// information the user wants to read.
			duration: opts.duration ?? 6000,
		});
	},
	info(message: string, opts: ToastOpts = {}) {
		return push({
			kind: "info",
			message,
			title: opts.title,
			duration: opts.duration ?? DEFAULT_DURATION,
		});
	},
	dismiss,
};

// ---------------------------------------------------------------------------
// React component — mount once near the app root.

export function Toaster() {
	const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	return (
		<div
			aria-live="polite"
			aria-atomic="true"
			className="fixed bottom-4 right-4 z-[100] flex flex-col-reverse gap-2 pointer-events-none"
			style={{ width: "min(380px, calc(100vw - 32px))" }}
		>
			{toasts.map((t) => (
				<ToastRow key={t.id} toast={t} />
			))}
		</div>
	);
}

function ToastRow({ toast: t }: { toast: Toast }) {
	const accent = accentFor(t.kind);
	return (
		<div
			role={t.kind === "error" ? "alert" : "status"}
			className="pointer-events-auto rounded-[12px] p-3 flex items-start gap-2.5 animate-in-toast"
			style={{
				background: "rgb(20 20 23 / 0.96)",
				backdropFilter: "blur(20px) saturate(180%)",
				WebkitBackdropFilter: "blur(20px) saturate(180%)",
				border: `1px solid ${accent.border}`,
				boxShadow:
					"0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
			}}
		>
			<div className="shrink-0 mt-0.5" style={{ color: accent.icon }}>
				{t.kind === "success" ? (
					<CheckCircle2 className="w-4 h-4" />
				) : t.kind === "error" ? (
					<AlertCircle className="w-4 h-4" />
				) : (
					<Info className="w-4 h-4" />
				)}
			</div>
			<div className="min-w-0 flex-1">
				{t.title && (
					<div className="text-[13px] font-medium leading-tight">{t.title}</div>
				)}
				<div
					className={`text-[12px] ${
						t.title ? "text-[var(--color-text-muted)] mt-0.5" : "text-[var(--color-text)]"
					} break-words`}
				>
					{t.message}
				</div>
			</div>
			<button
				type="button"
				onClick={() => dismiss(t.id)}
				className="shrink-0 text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors mt-0.5"
				aria-label="Dismiss"
			>
				<X className="w-3.5 h-3.5" />
			</button>
		</div>
	);
}

function accentFor(kind: ToastKind): { icon: string; border: string } {
	switch (kind) {
		case "success":
			return {
				icon: "var(--color-success)",
				border: "rgba(34, 197, 94, 0.22)",
			};
		case "error":
			return {
				icon: "var(--color-danger)",
				border: "rgba(239, 68, 68, 0.28)",
			};
		case "info":
		default:
			return {
				icon: "rgba(125,211,252,0.95)",
				border: "rgba(125, 211, 252, 0.22)",
			};
	}
}
